/**
 * DiningStaffLogin - Login page for kitchen and POS staff
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChefHat, CreditCard, Mail, Lock, Eye, EyeOff, LogIn } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import afterLogin, { extractRedirect } from '@/lib/afterLogin';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DiningStaffLogin = () => {
  const navigate = useNavigate();
  const [staffType, setStaffType] = useState('kitchen');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/dining/staff/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, staff_type: staffType })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Login failed');
      }

      const data = await res.json();
      
      // Store token and staff info
      // Also store in eventflow_token for compatibility with RequireAuth
      localStorage.setItem('eventflow_token', data.access_token);
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('dining_staff_token', data.access_token);
      localStorage.setItem('dining_staff_type', data.staff_type);
      localStorage.setItem('dining_staff_role', data.role);
      localStorage.setItem('dining_staff_name', data.name);
      localStorage.setItem('dining_staff_permissions', JSON.stringify(data.permissions));

      toast.success(`Welcome, ${data.name}!`);

      // The landing resolver is the single source of truth for where staff go.
      await afterLogin({
        navigate, userType: 'dining_staff', tenantId: null,
        redirect: extractRedirect() || '/dining/pos', token: data.access_token,
      });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center gap-4 mb-4">
            <div 
              className={`p-4 rounded-full cursor-pointer transition-all ${
                staffType === 'kitchen' 
                  ? 'bg-orange-500 text-white scale-110' 
                  : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
              }`}
              onClick={() => setStaffType('kitchen')}
              data-testid="kitchen-login-option"
            >
              <ChefHat className="h-8 w-8" />
            </div>
            <div 
              className={`p-4 rounded-full cursor-pointer transition-all ${
                staffType === 'pos' 
                  ? 'bg-blue-500 text-white scale-110' 
                  : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
              }`}
              onClick={() => setStaffType('pos')}
              data-testid="pos-login-option"
            >
              <CreditCard className="h-8 w-8" />
            </div>
          </div>
          <CardTitle className="text-2xl">
            {staffType === 'kitchen' ? 'Kitchen Staff Login' : 'POS Staff Login'}
          </CardTitle>
          <p className="text-gray-500">
            {staffType === 'kitchen' 
              ? 'Access the Kitchen Display System' 
              : 'Access the Point of Sale system'}
          </p>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="staff@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  data-testid="staff-email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10"
                  data-testid="staff-password-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <Button 
              type="submit" 
              className={`w-full h-12 text-lg ${
                staffType === 'kitchen' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-blue-500 hover:bg-blue-600'
              }`}
              disabled={loading}
              data-testid="staff-login-submit"
            >
              {loading ? (
                'Logging in...'
              ) : (
                <>
                  <LogIn className="h-5 w-5 mr-2" />
                  Sign In
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <a 
              href="/login" 
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Admin Login →
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DiningStaffLogin;
